import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Checkbox, FormControlLabel, Grid, Paper, Alert, Chip, Divider,
} from '@mui/material';
import { ArrowBack, Save, ArrowForward, Check, Assignment, Business } from '@mui/icons-material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { EmployeeSearchAutocomplete } from '@/components/employee/EmployeeSearchAutocomplete';
import { EmployeeAutofillSummary } from '@/components/employee/EmployeeAutofillSummary';
import type { EmployeeSearchResult } from '@/components/employee';
import { useDataProvider, useGetList } from 'react-admin';
import { stepValidator } from '@/components/wizard/validateWithZod';
import { buildTransitionChain } from '@/components/workflow';

interface PrefilledField {
  field: string;
  label: string;
  sourceValue: unknown;
  currentValue: unknown;
  confirmed: boolean;
  source?: 'employee' | 'relatedRecord';
}

const STEPS = [
  { id: 'personal', title: 'Personal Details' },
  { id: 'contact', title: 'Contact Info' },
  { id: 'role', title: 'Role & Department' },
  { id: 'employment', title: 'Employment Terms' },
  { id: 'statutory', title: 'Statutory Documents' },
  { id: 'popia', title: 'POPIA Consent' },
  { id: 'review', title: 'Review & Submit' },
];

const GENDERS = ['Male', 'Female', 'Other'];
const RACES = ['African', 'Coloured', 'Indian', 'White', 'Other'];

interface RoleTemplateInfo {
  id: string;
  roleTitle: string;
  roleCode: string;
  minimumNqfLevel: number | null;
  seniorityLevel: string | null;
  availableContractTypes: string[];
  statutoryDocumentRequirements: string[];
  dutyDefinitions: string[];
}

interface DepartmentInfo {
  id: string;
  departmentName: string;
  departmentCode: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  idNumber: string;
  dateOfBirth: string;
  gender: string;
  race: string;
  nationality: string;
  hasDisability: boolean;
  email: string;
  phone: string;
  address: string;
  position: string;
  departmentId: string;
  departmentName: string;
  roleTemplateId: string;
  roleTemplate: RoleTemplateInfo | null;
  contractType: string;
  startDate: string;
  targetStartDate: string;
  probationEndDate: string;
  hasRSAID: boolean;
  hasTaxNumber: boolean;
  hasBankDetails: boolean;
  hasWorkPermit: boolean;
  popiaConsent: boolean;
  popiaConsentDate: string;
  highestNqfLevel: string;
  dutyDefinitions: string[];
}

const INITIAL_VALUES: FormValues = {
  firstName: '', lastName: '', idNumber: '', dateOfBirth: '', gender: '', race: '',
  nationality: 'South African', hasDisability: false,
  email: '', phone: '', address: '',
  position: '', departmentId: '', departmentName: '', roleTemplateId: '', roleTemplate: null,
  contractType: '', startDate: '', targetStartDate: '', probationEndDate: '',
  hasRSAID: false, hasTaxNumber: false, hasBankDetails: false, hasWorkPermit: false,
  popiaConsent: false, popiaConsentDate: '', highestNqfLevel: '', dutyDefinitions: [],
};

const stepSchemas = {
  personal: z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    idNumber: z.string().regex(/^\d{13}$/, 'ID number must be 13 digits'),
    dateOfBirth: z.string().min(1, 'Date of birth is required'),
    gender: z.string().min(1, 'Gender is required'),
    race: z.string().min(1, 'Race is required'),
  }),
  contact: z.object({
    email: z.string().min(1, 'Email is required').email('Invalid email format'),
    phone: z.string().min(1, 'Phone is required'),
  }),
  role: z.object({
    departmentId: z.string().min(1, 'Department is required'),
    roleTemplateId: z.string().min(1, 'Role template is required'),
  }),
  employment: z.object({
    position: z.string().min(1, 'Position is required'),
    contractType: z.string().min(1, 'Contract type is required'),
    startDate: z.string().min(1, 'Start date is required'),
  }),
  statutory: z.object({
    hasRSAID: z.literal(true, { errorMap: () => ({ message: 'RSA ID is required' }) }),
    hasTaxNumber: z.literal(true, { errorMap: () => ({ message: 'Tax number is required' }) }),
  }),
  popia: z.object({
    popiaConsent: z.literal(true, { errorMap: () => ({ message: 'You must acknowledge POPIA consent to proceed' }) }),
  }),
};

const validateStep = (stepId: string, values: FormValues) => {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) return { isValid: true, errors: {} };
  return stepValidator(schema)(values as unknown as Record<string, unknown>);
};

export const OnboardingWizard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const employeeIdParam = searchParams.get('employeeId') || '';
  const employeeNameParam = searchParams.get('employeeName') || '';

  const { data: departmentData } = useGetList('departments', { pagination: { page: 1, perPage: 100 } });
  const { data: roleTemplateData } = useGetList('roletemplates', { pagination: { page: 1, perPage: 100 } });

  const departments = useMemo(() => {
    return (departmentData || []).map((d: { id: string; departmentName?: string; department?: { departmentName?: string; departmentCode?: string }; departmentCode?: string }) => ({
      id: d.id,
      departmentName: d.departmentName || d.department?.departmentName || '',
      departmentCode: d.departmentCode || d.department?.departmentCode || '',
    }));
  }, [departmentData]);

  const roleTemplates = useMemo(() => {
    return (roleTemplateData || []).map((t: { id: string; departmentId?: string; department?: { id?: string }; roleTitle?: string; roleCode?: string; minimumNqfLevel?: number | null; seniorityLevel?: string | null; availableContractTypes?: string[]; statutoryDocumentRequirements?: string[]; dutyDefinitions?: string[]; status?: string }) => ({
      id: t.id,
      departmentId: t.departmentId || t.department?.id || '',
      roleTitle: t.roleTitle || '',
      roleCode: t.roleCode || '',
      minimumNqfLevel: t.minimumNqfLevel || null,
      seniorityLevel: t.seniorityLevel || null,
      availableContractTypes: t.availableContractTypes || [],
      statutoryDocumentRequirements: t.statutoryDocumentRequirements || [],
      dutyDefinitions: t.dutyDefinitions || [],
      status: t.status || 'Active',
    }));
  }, [roleTemplateData]);

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const [prefilledFields, setPrefilledFields] = useState<PrefilledField[]>([]);

  const initialValues = useMemo(() => {
    if (employeeIdParam && employeeNameParam) {
      return {
        ...INITIAL_VALUES,
        employeeId: employeeIdParam,
        employeeName: employeeNameParam,
      };
    }
    return { ...INITIAL_VALUES, employeeId: '' };
  }, [employeeIdParam, employeeNameParam]);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      employeeId: null,
      targetStartDate: v.targetStartDate || v.startDate,
      departmentId: v.departmentId,
      roleTemplateId: v.roleTemplateId,
      position: v.position,
      contractType: v.contractType,
      minimumNqfLevel: v.roleTemplate?.minimumNqfLevel ?? null,
      statutoryDocumentRequirements: v.roleTemplate?.statutoryDocumentRequirements || [],
      seniorityLevel: v.roleTemplate?.seniorityLevel || null,
      dutyDefinitions: v.roleTemplate?.dutyDefinitions || [],
      popiaConsentDate: v.popiaConsent ? (v.popiaConsentDate || new Date().toISOString()) : null,
      documents: [
        ...(v.hasRSAID ? [{ type: 'rsa_id', verified: false }] : []),
        ...(v.hasTaxNumber ? [{ type: 'tax_number', verified: false }] : []),
        ...(v.hasBankDetails ? [{ type: 'bank_details', verified: false }] : []),
        ...(v.hasWorkPermit ? [{ type: 'work_permit', verified: false }] : []),
      ],
      nqfCompliant: v.highestNqfLevel
        ? parseInt(v.highestNqfLevel) >= (v.roleTemplate?.minimumNqfLevel || 0)
        : false,
      documentsCompliant: v.hasRSAID && v.hasTaxNumber,
    };

    const processResult = await dataProvider.create('onboarding-processes', { data: formValues });
    const processId = processResult.data.id;

    const wizardEvents = ['ASSIGN_ROLE', 'ROLE_VALID', 'DOCS_VERIFIED', 'NQF_COMPLIANT', 'CONTRACT_SIGNED', 'CONSENT_PROVIDED'];
    const { finalState, transitions } = buildTransitionChain('onboarding', 'initiated', wizardEvents);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'onboarding',
        entityId: processId,
        entityName: `${v.firstName} ${v.lastName}`,
        currentState: { value: finalState, context },
        status: 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: processId,
        transitions,
      },
    });

    return { id: processId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback, createdId,
    nextStep, prevStep, updateValues, validateStep: validate, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: initialValues as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const handleEmployeeSelect = useCallback((employee: EmployeeSearchResult) => {
    setSelectedEmployee(employee);

    const autofillData = {
      firstName: employee.demographics?.firstName || '',
      lastName: employee.demographics?.lastName || '',
      idNumber: '',
      dateOfBirth: '',
      gender: employee.demographics?.gender || '',
      race: employee.demographics?.race || '',
      email: '',
      phone: '',
      department: employee.organisationalHierarchy?.department || '',
      position: employee.seniority?.jobTitle || '',
      contractType: employee.contractType,
    };

    const fields: PrefilledField[] = [
      { field: 'firstName', label: 'First Name', sourceValue: employee.demographics?.firstName, currentValue: employee.demographics?.firstName, confirmed: true },
      { field: 'lastName', label: 'Last Name', sourceValue: employee.demographics?.lastName, currentValue: employee.demographics?.lastName, confirmed: true },
      { field: 'idNumber', label: 'ID Number', sourceValue: '', currentValue: '', confirmed: false },
      { field: 'dateOfBirth', label: 'Date of Birth', sourceValue: '', currentValue: '', confirmed: false },
      { field: 'gender', label: 'Gender', sourceValue: employee.demographics?.gender, currentValue: employee.demographics?.gender, confirmed: true },
      { field: 'race', label: 'Race', sourceValue: employee.demographics?.race, currentValue: employee.demographics?.race, confirmed: true },
      { field: 'email', label: 'Email', sourceValue: '', currentValue: '', confirmed: false },
      { field: 'phone', label: 'Phone', sourceValue: '', currentValue: '', confirmed: false },
      { field: 'department', label: 'Department', sourceValue: employee.organisationalHierarchy?.department, currentValue: employee.organisationalHierarchy?.department, confirmed: true },
      { field: 'position', label: 'Position', sourceValue: employee.seniority?.jobTitle, currentValue: employee.seniority?.jobTitle, confirmed: true },
      { field: 'contractType', label: 'Contract Type', sourceValue: employee.contractType, currentValue: employee.contractType, confirmed: true },
    ];

    setPrefilledFields(fields);
    updateValues(autofillData);
  }, [updateValues]);

  const handleClearEmployee = useCallback(() => {
    setSelectedEmployee(null);
    setPrefilledFields([]);
  }, []);

  useMemo(() => {
    if (employeeIdParam && employeeNameParam && !selectedEmployee) {
      const parts = employeeNameParam.split(' ');
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ') || '';
      updateValues({ firstName, lastName, employeeId: employeeIdParam });
    }
  }, [employeeIdParam, employeeNameParam, selectedEmployee, updateValues]);

  const vals = values as unknown as FormValues;

  const handleNext = () => {
    const valid = validate((v) => validateStep(currentStep.id, v as unknown as FormValues));
    if (!valid) return;
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleComplete = async () => {
    await complete();
  };

  const handleReset = () => {
    reset();
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'personal':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <EmployeeSearchAutocomplete
                label="Search Existing Employee"
                onEmployeeSelect={handleEmployeeSelect}
                onClear={handleClearEmployee}
              />
            </Grid>
            {selectedEmployee && (
              <Grid size={{ xs: 12 }}>
                <EmployeeAutofillSummary
                  fields={prefilledFields}
                  employeeName={selectedEmployee.fullName}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="First Name" value={values.firstName || ''} onChange={e => updateField('firstName', e.target.value)} fullWidth error={!!errors.firstName} helperText={errors.firstName} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Last Name" value={values.lastName || ''} onChange={e => updateField('lastName', e.target.value)} fullWidth error={!!errors.lastName} helperText={errors.lastName} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="ID Number (13 digits)" value={values.idNumber || ''} onChange={e => updateField('idNumber', e.target.value)} fullWidth error={!!errors.idNumber} helperText={errors.idNumber || 'South African 13-digit ID number'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Date of Birth" type="date" value={values.dateOfBirth || ''} onChange={e => updateField('dateOfBirth', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} error={!!errors.dateOfBirth} helperText={errors.dateOfBirth} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth error={!!errors.gender}>
                <InputLabel>Gender</InputLabel>
                <Select value={values.gender || ''} label="Gender" onChange={e => updateField('gender', e.target.value)}>
                  {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth error={!!errors.race}>
                <InputLabel>Race (for EE purposes)</InputLabel>
                <Select value={values.race || ''} label="Race (for EE purposes)" onChange={e => updateField('race', e.target.value)}>
                  {RACES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Nationality" value={values.nationality || ''} onChange={e => updateField('nationality', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel control={<Checkbox checked={!!values.hasDisability} onChange={e => updateField('hasDisability', e.target.checked)} />} label="Person with disability" />
            </Grid>
          </Grid>
        );

      case 'contact':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Email Address" type="email" value={values.email || ''} onChange={e => updateField('email', e.target.value)} fullWidth error={!!errors.email} helperText={errors.email} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Phone Number" value={values.phone || ''} onChange={e => updateField('phone', e.target.value)} fullWidth error={!!errors.phone} helperText={errors.phone || '+27 XX XXX XXXX format'} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Physical Address" value={values.address || ''} onChange={e => updateField('address', e.target.value)} fullWidth multiline rows={2} />
            </Grid>
          </Grid>
        );

      case 'role':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Select the department and role template for this employee. The role template will define minimum NQF requirements,
                permitted contract types, and any additional statutory document requirements.
              </Alert>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.departmentId}>
                <InputLabel>Department *</InputLabel>
                <Select
                  value={values.departmentId || ''}
                  label="Department *"
                  onChange={e => {
                    updateField('departmentId', e.target.value);
                    const dept = departments.find(d => d.id === e.target.value);
                    updateField('departmentName', dept?.departmentName || '');
                    updateField('roleTemplateId', '');
                    updateField('roleTemplate', null);
                  }}
                >
                  {departments.map(d => (
                    <MenuItem key={d.id} value={d.id}>{d.departmentName} ({d.departmentCode})</MenuItem>
                  ))}
                </Select>
                {errors.departmentId && <Typography variant="caption" color="error">{errors.departmentId}</Typography>}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.roleTemplateId}>
                <InputLabel>Role Template *</InputLabel>
                <Select
                  value={values.roleTemplateId || ''}
                  label="Role Template *"
                  onChange={e => {
                    const template = roleTemplates.find(t => t.id === e.target.value);
                    updateField('roleTemplateId', e.target.value);
                    updateField('roleTemplate', template || null);
                    updateField('position', template?.roleTitle || values.position);
                  }}
                  disabled={!values.departmentId}
                >
                  {roleTemplates
                    .filter(t => t.departmentId === values.departmentId && t.status === 'Active')
                    .map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">{t.roleTitle}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t.roleCode} | NQF Level {t.minimumNqfLevel || 'No minimum'} | {t.seniorityLevel || 'No seniority'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
                {errors.roleTemplateId && <Typography variant="caption" color="error">{errors.roleTemplateId}</Typography>}
              </FormControl>
              {!values.roleTemplateId && Boolean(values.departmentId) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  No active role templates for this department. <a href="/role-templates/create" style={{ color: '#0047AB' }}>Create one here</a>.
                </Typography>
              )}
            </Grid>

            {(values.roleTemplate as RoleTemplateInfo) && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assignment fontSize="small" /> Role Template Requirements
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Minimum NQF Level</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {(values.roleTemplate as RoleTemplateInfo).minimumNqfLevel ? `Level ${(values.roleTemplate as RoleTemplateInfo).minimumNqfLevel}` : 'No minimum'}
                    </Typography>
                    {(values.roleTemplate as RoleTemplateInfo).minimumNqfLevel && (
                      <TextField
                        label="Employee's Highest NQF"
                        type="number"
                        value={values.highestNqfLevel}
                        onChange={e => updateField('highestNqfLevel', e.target.value)}
                        size="small"
                        sx={{ mt: 1 }}
                        inputProps={{ min: 1, max: 10 }}
                        helperText={(parseInt(String(values.highestNqfLevel || '0')) as number) >= ((values.roleTemplate as RoleTemplateInfo)?.minimumNqfLevel || 0) ? 'Compliant' : 'Below minimum — requires waiver'}
                        error={(parseInt(String(values.highestNqfLevel || '0')) as number) < ((values.roleTemplate as RoleTemplateInfo)?.minimumNqfLevel || 0)}
                      />
                    )}
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Permitted Contract Types</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {(values.roleTemplate as RoleTemplateInfo).availableContractTypes.map((ct: string) => (
                        <Chip key={ct} label={ct} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Seniority Level</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {(values.roleTemplate as RoleTemplateInfo).seniorityLevel || 'Not specified'}
                    </Typography>
                  </Paper>
                </Grid>
                {(values.roleTemplate as RoleTemplateInfo).statutoryDocumentRequirements.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.50' }}>
                      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600 }}>
                        Additional Statutory Document Requirements for this Role:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {(values.roleTemplate as RoleTemplateInfo).statutoryDocumentRequirements.map((req: string) => (
                          <Chip key={req} label={req} size="small" color="warning" variant="outlined" />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Position / Job Title"
                value={values.position || ''}
                onChange={e => updateField('position', e.target.value)}
                fullWidth
                error={!!errors.position}
                helperText={errors.position || 'Auto-filled from role template, or enter manually'}
              />
            </Grid>
          </Grid>
        );

      case 'employment':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Position / Job Title" value={values.position || ''} onChange={e => updateField('position', e.target.value)} fullWidth error={!!errors.position} helperText={errors.position} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select value={String(values.departmentName || '')} label="Department" disabled>
                  <MenuItem value={String(values.departmentName || '')}><Typography component="span">{String(values.departmentName || 'Selected in Role step')}</Typography></MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth error={!!errors.contractType}>
                <InputLabel>Contract Type *</InputLabel>
                <Select value={values.contractType || ''} label="Contract Type *" onChange={e => updateField('contractType', e.target.value)}>
                  {(values.roleTemplate as RoleTemplateInfo)?.availableContractTypes?.length > 0 ? (
                    (values.roleTemplate as RoleTemplateInfo).availableContractTypes.map((c: string) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))
                  ) : (
                    ['Permanent', 'FixedTerm', 'PartTime', 'Seasonal', 'IndependentContractor'].map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))
                  )}
                </Select>
                {errors.contractType && <Typography variant="caption" color="error">{errors.contractType}</Typography>}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Start Date" type="date" value={values.startDate || ''} onChange={e => updateField('startDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} error={!!errors.startDate} helperText={errors.startDate} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Probation End Date" type="date" value={values.probationEndDate || ''} onChange={e => updateField('probationEndDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} error={!!errors.probationEndDate} helperText={errors.probationEndDate} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">BCEA requires written contract within probation period. Employment terms will be recorded per BCEA requirements.</Alert>
            </Grid>
          </Grid>
        );

case 'statutory': {
        const roleTemplateReqs = vals.roleTemplate?.statutoryDocumentRequirements || [];
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Upload and verify statutory documents.
                {roleTemplateReqs.length > 0 && (
                  <Typography variant="body2" component="span" sx={{ color: 'warning.main', fontWeight: 500 }}>
                    {' '}Additional documents required for this role: {roleTemplateReqs.join(', ')}
                  </Typography>
                )}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel control={<Checkbox checked={!!vals.hasRSAID} onChange={e => updateField('hasRSAID', e.target.checked)} />} label="RSA Identity Document" />
              {errors.hasRSAID && <Typography variant="caption" color="error">{errors.hasRSAID}</Typography>}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel control={<Checkbox checked={!!vals.hasTaxNumber} onChange={e => updateField('hasTaxNumber', e.target.checked)} />} label="SARS Tax Number" />
              {errors.hasTaxNumber && <Typography variant="caption" color="error">{errors.hasTaxNumber}</Typography>}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel control={<Checkbox checked={!!vals.hasBankDetails} onChange={e => updateField('hasBankDetails', e.target.checked)} />} label="Bank Details (for FICA)" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel control={<Checkbox checked={!!vals.hasWorkPermit} onChange={e => updateField('hasWorkPermit', e.target.checked)} />} label="Work Permit (if foreign)" />
            </Grid>
            {roleTemplateReqs.includes('Police Clearance Certificate') && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Police Clearance Certificate is required for this role template (NQF compliance)
                </Alert>
              </Grid>
            )}
            {roleTemplateReqs.includes('Credit Check') && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel control={<Checkbox checked={false} onChange={() => {}} />} label="Credit Check (required for role)" />
              </Grid>
            )}
            {roleTemplateReqs.includes('Reference Checks') && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel control={<Checkbox checked={false} onChange={() => {}} />} label="Reference Checks (required for role)" />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">FICA requires verification of identity and bank details. Documents will be retained per POPIA requirements.</Alert>
            </Grid>
          </Grid>
        );
      }

      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="success" sx={{ mb: 2 }}>Review all information before submitting. Once submitted, an onboarding process will be initiated.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Personal Details</Typography>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                <Typography variant="body2"><strong>Name:</strong> {vals.firstName} {vals.lastName}</Typography>
                <Typography variant="body2"><strong>ID:</strong> {vals.idNumber}</Typography>
                <Typography variant="body2"><strong>DOB:</strong> {vals.dateOfBirth}</Typography>
                <Typography variant="body2"><strong>Gender:</strong> {vals.gender}</Typography>
                <Typography variant="body2"><strong>Race:</strong> {vals.race}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Role & Department</Typography>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                <Typography variant="body2"><strong>Role Template:</strong> {vals.roleTemplate?.roleTitle || 'Not selected'}</Typography>
                <Typography variant="body2"><strong>Department:</strong> {vals.departmentName}</Typography>
                <Typography variant="body2"><strong>Min NQF:</strong> {vals.roleTemplate?.minimumNqfLevel ? `Level ${vals.roleTemplate.minimumNqfLevel}` : 'No minimum'}</Typography>
                <Typography variant="body2"><strong>Seniority:</strong> {vals.roleTemplate?.seniorityLevel || 'Not specified'}</Typography>
                {vals.highestNqfLevel && parseInt(vals.highestNqfLevel) < (vals.roleTemplate?.minimumNqfLevel || 0) && (
                  <Alert severity="warning" sx={{ mt: 1 }}>Employee NQF level below role template minimum — waiver required</Alert>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Employment</Typography>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                <Typography variant="body2"><strong>Position:</strong> {vals.position}</Typography>
                <Typography variant="body2"><strong>Contract:</strong> {vals.contractType}</Typography>
                <Typography variant="body2"><strong>Start:</strong> {vals.startDate}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact</Typography>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                <Typography variant="body2"><strong>Email:</strong> {vals.email}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {vals.phone}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Statutory Documents</Typography>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                <Typography variant="body2"><strong>RSA ID:</strong> {vals.hasRSAID ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>Tax Number:</strong> {vals.hasTaxNumber ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>Bank Details:</strong> {vals.hasBankDetails ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><strong>POPIA Consent:</strong> {vals.popiaConsent ? 'Yes' : 'No'}</Typography>
              </Box>
            </Grid>
            {vals.roleTemplate && (vals.roleTemplate as RoleTemplateInfo).statutoryDocumentRequirements.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Role-Specific Requirements</Typography>
                <Box sx={{ bgcolor: 'warning.50', p: 2, borderRadius: 1 }}>
                  {(vals.roleTemplate as RoleTemplateInfo).statutoryDocumentRequirements.map((req: string) => (
                    <Chip key={req} label={req} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        );

      case 'popia':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>POPIA requires explicit consent for collecting and processing personal information.</Alert>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2">
                  By clicking below, I consent to Rosta collecting, storing, and processing my personal information
                  for the purpose of employment and compliance with South African labour law. I understand that I have
                  the right to access, correct, or request deletion of my personal information.
                </Typography>
              </Paper>
              <FormControlLabel control={<Checkbox checked={!!values.popiaConsent} onChange={e => updateField('popiaConsent', e.target.checked)} />} label="I acknowledge and consent to the processing of my personal information (required)" />
              {errors.popiaConsent && <Typography variant="caption" color="error" display="block">{errors.popiaConsent}</Typography>}
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  const stepDescriptions: Record<string, string> = {
    personal: 'Capture employee personal details as per employment equity requirements',
    contact: 'Capture contact information for communication purposes',
    role: 'Select department and role template to define position requirements, contract type, and NQF qualifications',
    employment: 'Define employment terms including position, contract type and start date',
    statutory: 'Track required statutory documents (RSA ID, Tax Number, Bank Details) plus role-specific requirements',
    popia: 'Obtain POPIA consent for processing personal information',
    review: 'Review all entered information before creating the employee record',
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={handleComplete}
      onReset={handleReset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Employee Onboarding Initiated"
          processId={createdId || undefined}
          fields={[
            { label: 'Employee Name', value: `${vals.firstName} ${vals.lastName}` },
            { label: 'ID Number', value: vals.idNumber },
            { label: 'Role Template', value: vals.roleTemplate?.roleTitle || 'Not selected' },
            { label: 'Department', value: vals.departmentName },
            { label: 'Position', value: vals.position },
            { label: 'Contract Type', value: vals.contractType },
            { label: 'Min NQF Level', value: vals.roleTemplate?.minimumNqfLevel ? `Level ${vals.roleTemplate.minimumNqfLevel}` : 'No minimum' },
            { label: 'Start Date', value: vals.startDate },
            { label: 'Email', value: vals.email },
          ]}
          footerActions={[
            { label: 'View Onboarding Process', to: '/onboarding-processes' },
            { label: 'Start Another', onClick: handleReset },
          ]}
        />
      ) : (
        <>
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          <WizardStep
            stepNumber={currentStepIndex + 1}
            totalSteps={totalSteps}
            title={currentStep.title}
            description={stepDescriptions[currentStep.id]}
          >
            {renderFormFields()}
          </WizardStep>
        </>
      )}
    </WizardLayout>
  );
};